import { Button, Stack, Typography } from "@mui/material";
import { useUpdateSettlementPoolBoostMutation } from "Api/Mutations/Settlements/useUpdateSettlementPoolBoostMutation";
import { PoolTotalDisplay } from "Components/Settlements/PoolBoost/PoolTotalDisplay";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import {
  getStardustBoostFormSchema,
  type StardustBoostFormData,
} from "Components/Settlements/PoolBoost/settlementPoolBoostFormSchema";
import {
  type CurrencyCode,
  LoyaltyPointType,
  type Region,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const RESOURCES = Resources.Admin.SettlementPoolBoosts;

type Props = {
  region: Region;
  currencyCode: CurrencyCode;
  boostAmount: number;
  settlementPoolBoostID: number;
};

export const StardustBoostCard = ({
  region,
  currencyCode,
  boostAmount,
  settlementPoolBoostID,
}: Props) => {
  const { t } = useResource();

  const { mutate: updateBoost, isPending } =
    useUpdateSettlementPoolBoostMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.SaveSuccess));
      },
    });

  const formValues = { boostAmount };

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<StardustBoostFormData>({
    resolver: zodResolver(getStardustBoostFormSchema(t)),
    defaultValues: formValues,
  });

  const onSubmit = ({ boostAmount }: StardustBoostFormData) => {
    updateBoost({
      id: settlementPoolBoostID,
      data: { boostAmount },
    });
  };

  return (
    <PrimaryPaper>
      <Stack gap={3} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" fontWeight={600}>
          {t(RESOURCES.StardustBoost)}
        </Typography>

        <PoolTotalDisplay
          region={region}
          loyaltyPointType={LoyaltyPointType.Stardust}
          currencyCode={currencyCode}
        />

        <FormInput
          control={control}
          name="boostAmount"
          label={t(RESOURCES.BoostAmount, { currency: currencyCode })}
          isNumber
          min={0}
          disabled={isPending}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={isPending || !isDirty}
          sx={{ alignSelf: "flex-start" }}
        >
          {t(RESOURCES.SaveButton)}
        </Button>
      </Stack>
    </PrimaryPaper>
  );
};
